import { Component, computed, effect, input, output, Type, viewChild, ViewContainerRef } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AssistantType } from '@vendasta/ai-assistants';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import {
  DEFAULT_CRM_UPDATE_FROM_MEETING_GOAL_ID,
  DEFAULT_LEAD_CAPTURE_GOAL_ID,
} from '../../../core/ai-assistant.constants';
import { AiCapabilityForm, AiToolForm, CustomCapabilityConfigurationForm } from '../../../core/forms';
import { DismissibleInfoBannerComponent } from '../../shared/dismissible-info-banner/dismissible-info-banner.component';
import { AiToolsListComponent } from '../../tools/ai-tools-list/ai-tools-list.component';
import { CrmUpdateCapabilityFormComponent } from './custom-forms/crm-update-capability-form/crm-update-capability-form.component';
import { LeadCaptureCapabilityFormComponent } from './custom-forms/lead-capture-capability-form/lead-capture-capability-form.component';

export const AiCapabilityInlineForms: { [goalId: string]: Type<CustomCapabilityConfigurationForm> | undefined } = {
  [DEFAULT_LEAD_CAPTURE_GOAL_ID]: LeadCaptureCapabilityFormComponent,
  [DEFAULT_CRM_UPDATE_FROM_MEETING_GOAL_ID]: CrmUpdateCapabilityFormComponent,
};

@Component({
  selector: 'ai-capability-form',
  imports: [
    GalaxyFormFieldModule,
    TranslateModule,
    MatInputModule,
    ReactiveFormsModule,
    AiToolsListComponent,
    MatIcon,
    GalaxyTooltipModule,
    MatCardModule,
    DismissibleInfoBannerComponent,
  ],
  templateUrl: './ai-capability-form.component.html',
  styleUrl: './ai-capability-form.component.scss',
})
export class AiCapabilityFormComponent {
  readonly capabilityForm = input.required<AiCapabilityForm>();
  readonly allowRemovingAnyTools = input<boolean>(false);
  readonly assistantType = input<AssistantType>();
  readonly toolFormDisplay = output<AiToolForm | undefined>();
  readonly promptModules = computed(() => this.capabilityForm().controls.promptModules.controls);
  readonly disableConfigurableGoalFields = input<boolean>(false);

  readonly inlineCustomForm = computed(() => {
    const form = this.capabilityForm();

    if (!form) {
      return undefined;
    }

    const goalId = form.controls.goalId?.value || '';
    const overrideOfId = form.controls.overrideOf?.value?.id || '';

    return AiCapabilityInlineForms?.[goalId] || AiCapabilityInlineForms?.[overrideOfId];
  });

  readonly inlineFormContainer = viewChild('inlineFormContainer', { read: ViewContainerRef });

  constructor() {
    effect(() => {
      const inlineForm = this.inlineCustomForm();
      const container = this.inlineFormContainer();

      container?.clear();

      if (inlineForm && container) {
        const componentRef = container.createComponent(inlineForm);
        if (componentRef) {
          componentRef.setInput('configurationForm', this.capabilityForm().controls.configuration);
          componentRef.setInput('goalId', this.capabilityForm().controls.goalId.value);
          componentRef.setInput('disableConfigurableGoalFields', this.disableConfigurableGoalFields());
        }
      }
    });
  }
}
